import { Fragment, ReactElement, ReactNode } from 'react';
import { Dialog as HeadlessDialog, Transition } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useInterruptibleTransition } from '../../hooks/useInterruptibleTransition';

interface MobileDrawerProps {
    open: boolean;
    onClose: () => void;
    children: ReactNode;
}

/**
 * Mobile slide-out drawer that appears from the left side.
 * Uses HeadlessUI Dialog for accessibility (focus trapping, Escape to close).
 * Only visible on mobile (< lg breakpoint).
 * A close during the slide-in hides the drawer at once; an open during the
 * slide-out restarts the slide-in (see `useInterruptibleTransition`).
 */
export function MobileDrawer({ open, onClose, children }: MobileDrawerProps): ReactElement {
    const { key, ...transitionProps } = useInterruptibleTransition(open);

    return (
        <Transition key={key} show={open} as={Fragment} {...transitionProps}>
            <HeadlessDialog as="div" className="relative z-50 lg:hidden" onClose={onClose}>
                {/* Backdrop */}
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-black/25 dark:bg-black/50" />
                </Transition.Child>

                {/* Drawer container */}
                <div className="fixed inset-0 flex">
                    {/* Drawer panel - slides in from left */}
                    <Transition.Child
                        as={Fragment}
                        enter="transform transition ease-in-out duration-300"
                        enterFrom="-translate-x-full"
                        enterTo="translate-x-0"
                        leave="transform transition ease-in-out duration-200"
                        leaveFrom="translate-x-0"
                        leaveTo="-translate-x-full"
                    >
                        <HeadlessDialog.Panel className="relative flex w-full max-w-xs flex-col bg-white dark:bg-darker">
                            {children}
                        </HeadlessDialog.Panel>
                    </Transition.Child>

                    {/* Close button positioned in the backdrop area */}
                    <Transition.Child
                        as={Fragment}
                        enter="ease-out duration-300"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="ease-in duration-200"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                    >
                        <div className="shrink-0 pt-4 pl-4">
                            <button
                                type="button"
                                className="rounded-full p-2 text-white/80 hover:text-white hover:bg-white/10 transition-colors"
                                onClick={onClose}
                                aria-label="Close menu"
                            >
                                <XMarkIcon className="h-6 w-6" />
                            </button>
                        </div>
                    </Transition.Child>
                </div>
            </HeadlessDialog>
        </Transition>
    );
}
